import React from "react";

export default function Terms() {
    return (
        <main className="max-w-4xl mx-auto px-4 py-8 text-slate-700">
            <h2 className="text-2xl font-bold mb-6">Terms and Conditions</h2>
            <p className="text-sm text-slate-500 mb-6">Last updated September 06, 2022</p>

            {/* Table of Contents */}
            <h3 className="text-xl font-semibold mt-6 mb-3">Table of Contents</h3>
            <ol className="list-decimal ml-6 space-y-1">
                <li>Agreement to Terms</li>
                <li>Intellectual Property Rights</li>
                <li>User Representations</li>
                <li>Prohibited Activities</li>
                <li>User Generated Contributions</li>
                <li>Contribution License</li>
                <li>Submissions</li>
                <li>Site Management</li>
                <li>Privacy Policy</li>
                <li>Term and Termination</li>
                <li>Modifications and Interruptions</li>
                <li>Governing Law</li>
                <li>Dispute Resolution</li>
                <li>Corrections</li>
                <li>Disclaimer</li>
                <li>Limitations of Liability</li>
                <li>Indemnification</li>
                <li>User Data</li>
                <li>Electronic Communications, Transactions, and Signatures</li>
                <li>Miscellaneous</li>
                <li>Contact Us</li>
            </ol>

            {/* Section 1 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">1. Agreement to Terms</h3>
            <p className="mb-4">
                These Terms of Use constitute a legally binding agreement made between you, whether
                personally or on behalf of an entity (“you”) and Clayton Car Contracts Ltd
                ("Company," “we," “us," or “our”), concerning your access to and use of the
                https://www.contracthire.net website as well as any other media form,
                media channel, mobile website or mobile application related, linked, or otherwise
                connected thereto (collectively, the “Site”).
            </p>
            <p className="mb-4">
                By accessing the Site, you agree to be bound by these Terms. If you do not agree,
                you are expressly prohibited from using the Site and must discontinue use immediately.
            </p>
            <p className="mb-4">
                Supplemental terms and conditions or documents that may be posted on the Site
                from time to time are incorporated herein by reference. We reserve the right,
                in our sole discretion, to make changes or modifications to these Terms of Use
                at any time and for any reason.
            </p>

            {/* Section 2 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">2. Intellectual Property Rights</h3>
            <p className="mb-4">
                Unless otherwise indicated, the Site is our proprietary property and all source
                code, databases, functionality, software, website designs, audio, video, text,
                photographs, and graphics on the Site (collectively, the “Content”) and the
                trademarks, service marks, and logos contained therein (the “Marks”) are owned
                or controlled by us or licensed to us.
            </p>
            <p className="mb-4">
                Provided you are eligible to use the Site, you are granted a limited license to
                access and use the Site for personal, non-commercial use. No Content or Marks
                may be copied, reproduced, republished, or exploited for commercial purposes
                without our express written permission.
            </p>

            {/* Section 3 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">3. User Representations</h3>
            <p className="mb-4">
                By using the Site, you represent and warrant that:
            </p>
            <ul className="list-disc ml-6 space-y-2 mb-4">
                <li>You have the legal capacity and agree to comply with these Terms of Use.</li>
                <li>You are not a minor in the jurisdiction in which you reside.</li>
                <li>You will not access the Site through automated or non-human means.</li>
                <li>You will not use the Site for any illegal or unauthorized purpose.</li>
                <li>Your use of the Site will not violate any applicable law or regulation.</li>
            </ul>

            {/* Section 4 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">4. Prohibited Activities</h3>
            <p className="mb-4">You may not access or use the Site for any purpose other than that for which we make it available. Prohibited activities include, but are not limited to:</p>
            <ul className="list-disc ml-6 space-y-2 mb-4">
                <li>Scraping, data mining, or unauthorized compilation of content.</li>
                <li>Tricking, defrauding, or misleading us or other users.</li>
                <li>Circumventing, disabling, or interfering with security features.</li>
                <li>Uploading malware, spam, or malicious code.</li>
                <li>Harassment, impersonation, or abusive behavior.</li>
                <li>Reverse engineering or unauthorized framing/linking.</li>
                <li>Using the Site for unauthorized commercial purposes.</li>
            </ul>

            {/* Section 5 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">5. User Generated Contributions</h3>
            <p className="mb-4">
                The Site does not offer users to submit or post content. However, if contributions
                are made available (text, video, audio, graphics, comments, suggestions, personal
                information, etc.), they may be viewable by other users and through third-party
                websites. You are solely responsible for your Contributions.
            </p>

            {/* Section 6 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">6. Contribution License</h3>
            <p className="mb-4">
                By submitting Contributions, you grant us the right to access, store, process,
                and use them in accordance with the Privacy Policy. You retain ownership of your
                Contributions, but grant us a license to use them for legitimate business purposes.
            </p>

            {/* Section 7 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">7. Submissions</h3>
            <p className="mb-4">
                Any questions, comments, suggestions, ideas, feedback, or other information you
                submit to us (“Submissions”) shall become our sole property. You waive all moral
                rights and agree we may use Submissions for any lawful purpose without
                compensation to you.
            </p>

            {/* Section 8 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">8. Site Management</h3>
            <p className="mb-4">
                We reserve the right to monitor the Site for violations of these Terms, take
                legal action, refuse or restrict access, disable contributions, and otherwise
                manage the Site to protect our rights and ensure proper functioning.
            </p>

            {/* Section 9 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">9. Privacy Policy</h3>
            <p className="mb-4">
                We care about data privacy and security. Please review our Privacy Policy at{" "}
                <a
                    href="https://contracthire.net/privacy"
                    className="text-blue-600 underline"
                >
                    https://contracthire.net/privacy
                </a>.
            </p>

            {/* Section 10 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">10. Term and Termination</h3>
            <p className="mb-4">
                These Terms remain in full force while you use the Site. We may suspend or
                terminate your access at any time, for any reason, including violations of these
                Terms. You are prohibited from creating a new account after termination.
            </p>

            {/* Section 11 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">11. Modifications and Interruptions</h3>
            <p className="mb-4">
                We reserve the right to change, modify, or discontinue all or part of the Site
                at any time without notice. We cannot guarantee the Site will always be available
                and are not liable for downtime, errors, or interruptions.
            </p>

            {/* Section 12 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">12. Governing Law</h3>
            <p className="mb-4">
                These Terms are governed by the laws of the United Kingdom. If you are an EU
                consumer, you also have protections under your country of residence. Both parties
                agree to submit to the non-exclusive jurisdiction of the UK courts.
            </p>

            {/* Section 13 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">13. Dispute Resolution</h3>
            <p className="mb-4">
                The European Commission provides an online dispute resolution platform:{" "}
                <a
                    href="https://ec.europa.eu/consumers/odr"
                    className="text-blue-600 underline"
                >
                    https://ec.europa.eu/consumers/odr
                </a>. Please contact us to raise any disputes.
            </p>

            {/* Section 14 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">14. Corrections</h3>
            <p className="mb-4">
                There may be information on the Site containing typographical errors, inaccuracies,
                or omissions. We reserve the right to correct or update such information without notice.
            </p>

            {/* Section 15 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">15. Disclaimer</h3>
            <p className="mb-4">
                The Site is provided on an “as-is” and “as-available” basis. We disclaim all warranties,
                express or implied, including merchantability, fitness for a particular purpose, and
                non-infringement. Use the Site at your own risk.
            </p>

            {/* Section 16 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">16. Limitations of Liability</h3>
            <p className="mb-4">
                In no event will we or our employees be liable to you or any third party for damages
                arising from your use of the Site, including lost profit, data, or revenue, even if
                advised of the possibility of such damages.
            </p>

            {/* Section 17 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">17. Indemnification</h3>
            <p className="mb-4">
                You agree to indemnify and hold us harmless from any loss, damage, or liability
                resulting from your use of the Site, breach of these Terms, or violation of another’s
                rights. We reserve the right to assume exclusive defense of such claims.
            </p>

            {/* Section 18 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">18. User Data</h3>
            <p className="mb-4">
                We may maintain data you transmit for performance of the Site. Although we perform
                routine backups, you are solely responsible for your own data. We are not liable for
                loss or corruption of such data.
            </p>

            {/* Section 19 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">19. Electronic Communications, Transactions, and Signatures</h3>
            <p className="mb-4">
                By using the Site, you consent to electronic communications. You agree that electronic
                agreements, notices, and records satisfy legal requirements for written communications.
                You waive any rights requiring non-electronic records or signatures.
            </p>

            {/* Section 20 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">20. Miscellaneous</h3>
            <p className="mb-4">
                These Terms constitute the entire agreement between you and us. If any provision is
                deemed unlawful or unenforceable, it shall not affect the validity of the remaining
                provisions. Our failure to enforce any right is not a waiver of such right.
            </p>

            {/* Section 21 */}
            <h3 className="text-xl font-semibold mt-6 mb-3">21. Contact Us</h3>
            <p className="mb-4">
                Clayton Car Contracts Ltd <br />
                Brook Farm Barn <br />
                Stafford, Marston ST18 9SX <br />
                United Kingdom <br />
                Phone: 01785 716133 <br />
                Email:{" "}
                <a href="mailto:sean@contracthire.net" className="text-blue-600 underline">
                    sean@contracthire.net
                </a>
            </p>
        </main>
    );
}
